import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import { Checkbox } from '@gasbuddy/react-components';
import CardWithEditModal from '../../card-with-edit-modal';
import FuelPriceContent from './fuel-price-content';
import Notification from '../../notification';
import StationPropType from '../../../utils/prop-types/station';
import { editStation } from '../../../actions';
import BpUtils from '../../../../lib/bputils';

import styles from './styles.css';

const PRICE_UPDATE_THRESHOLD_MINUTES = 15;

const { api } = require('../../../../lib/bputils').default;
const { canEditStationFuelPrices } = require('../../../../lib/permissions').default;

const featuresHasCashPrices = features => !!features.find(f => f.id === 'cash_credit');

/* eslint-disable max-len */
const onSaveConfirmationDialogProps = {
  title: 'Confirm Price Submission',
  saveButtonText: 'Submit Prices',
  children: (
    <div className={styles.priceConfirmationDialogContent}>
      By submitting these prices Business Pages will periodically update your station with these price(s) for the remainder of the day. Prices will be submitted hourly. Existing prices that are less than two hours old and match your updated price will not be over-written.
    </div>
  ),
};
/* eslint-enable max-len */

function StationFuelPriceCard({
  isLoading: isLoadingProps,
  station,
  editStation: dispatchEditStation,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [prices, setPrices] = React.useState([]);
  const [currentStationPrices, setCurrentStationPrices] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(isLoadingProps);
  const [showPricesBeingUpdated, setShowPricesBeingUpdated] = React.useState(false);
  const [hasCashPrices, setHasCashPrices] = React.useState(featuresHasCashPrices(station.features));
  const [recurringPrices, setRecurringPrices] = React.useState(false);

  const showEditButton = canEditStationFuelPrices();

  React.useEffect(() => {
    setIsLoading(isLoadingProps);
  }, [isLoadingProps]);

  React.useEffect(() => {
    setHasCashPrices(featuresHasCashPrices(station.features));
    const { currentStationPrices: newPrices, showPricesBeingUpdated: arePricesBeingUpdated } = getCurrentStationPrices();
    setCurrentStationPrices(newPrices);
    setShowPricesBeingUpdated(arePricesBeingUpdated);
    setPrices(zeroOutPricesState(newPrices));
  }, [station.id]);


  const toggleIsEditing = () => setIsEditing(!isEditing);

  const handleOnModalClose = () => {
    toggleIsEditing();
    setPrices(zeroOutPricesState(station.prices));
    setRecurringPrices(false);
  };

  const formatPricesForSubmitting = () => prices.reduce((result, p) => {
    if (p.credit?.price) {
      result.push({ station_id: station.id, fuel_product: p.fuel_product, price: p.credit.price, price_type: 'credit' });
    }
    if (p.cash?.price && station.features.find(f => f.id === 'cash_credit')) {
      result.push({ station_id: station.id, fuel_product: p.fuel_product, price: p.cash.price, price_type: 'cash' });
    }
    return result;
  }, []);

  const handleOnSave = () => {
    const newPrices = filterOutZeroPrices(prices);
    setIsLoading(true);
    toggleIsEditing();
    const formattedPrices = formatPricesForSubmitting();
    if (formattedPrices.length) {
      api.put({
        path: `/stations/${station.id}/prices${recurringPrices ? '?recurring_report=true' : ''}`,
        data: formattedPrices,
      })
        .then(() => {
          BpUtils.store.set(`station_prices_${station.id}`, { saved_at: moment().utc().format(), prices: newPrices });
          const { currentStationPrices: newStationPrices, showPricesBeingUpdated: newShowPricesBeingUpdated } = getCurrentStationPrices();
          dispatchEditStation({ prices: newStationPrices });
          setIsLoading(false);
          setPrices(zeroOutPricesState(prices));
          setCurrentStationPrices(newStationPrices);
          setShowPricesBeingUpdated(newShowPricesBeingUpdated);
          setRecurringPrices(false);
          Notification.success('Prices have been submitted and will be updated on GasBuddy within 15 minutes');
        })
        .catch(() => Notification.error('There was a problem updating station prices. Please try again.'));
    } else {
      // pretend we submit the price
      Notification.success('Successfully updated the station\'s prices');
      setTimeout(() => setIsLoading(false), 1000);
    }
  };

  const onPriceBlur = ({ price, fuel_product: fuelProduct, price_type: priceType }) => {
    // must deep clone because you shouldn't change the state directly or weird things happen
    const deepCloneOfStatePrices = JSON.parse(JSON.stringify(prices));
    const priceToEdit = deepCloneOfStatePrices.find(p => p.fuel_product === fuelProduct)[priceType];
    priceToEdit.price = price;
    priceToEdit.nickname = 'Owner';
    priceToEdit.stamp = 'owner';
    priceToEdit.posted_time = moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ');

    setPrices(deepCloneOfStatePrices);
  };

  const minifyPricesArray = pricesArr => pricesArr.map(p => ({
    type: p.fuel_product,
    ...(p.cash?.price && { cash: p.cash?.price, cashPostedTime: moment(p.cash?.posted_time).utc().format('YYYY-MM-DDTHH:mm') }) || {},
    ...(p.credit?.price && { credit: p.credit?.price, creditPostedTime: moment(p.credit?.posted_time).utc().format('YYYY-MM-DDTHH:mm') }) || {},
  }));

  const arePricesSameInCache = (pricesArr, cachedPrices) => {
    const minifiedPrices = minifyPricesArray(pricesArr);
    const minifiedCachedPrices = minifyPricesArray(cachedPrices);

    return !minifiedCachedPrices.some((p) => {
      if (p.cash
        && ((p.cash !== (minifiedPrices.find(f => f.type === p.type) || {}).cash)
        || (p.cashPostedTime !== (minifiedPrices.find(f => f.type === p.type) || {}).cashPostedTime))) {
        return true;
      }
      if (p.credit
        && ((p.credit !== (minifiedPrices.find(f => f.type === p.type) || {}).credit)
        || (p.creditPostedTime !== (minifiedPrices.find(f => f.type === p.type) || {}).creditPostedTime))) {
        return true;
      }
      return false;
    });
  };

  const getCurrentStationPrices = () => {
    const { id: stationId, prices: currentPrices } = station;
    const cachedPrices = BpUtils.store.get(`station_prices_${stationId}`);
    if (cachedPrices) {
      const diff = moment().utc().diff(cachedPrices.saved_at, 'minutes');
      if (diff > PRICE_UPDATE_THRESHOLD_MINUTES || arePricesSameInCache(currentPrices, cachedPrices.prices)) {
        BpUtils.store.remove(`station_prices_${stationId}`);
      } else {
        const overwrittenStationPrices = overwriteStationPricesWithCachedPrices(currentPrices, cachedPrices);
        return { currentStationPrices: overwrittenStationPrices, showPricesBeingUpdated: true };
      }
    }
    return { currentStationPrices: currentPrices, showPricesBeingUpdated: false };
  };

  const overwriteStationPricesWithCachedPrices = (pricesArr, cachedObject) => {
    const deepCloneOfPrices = JSON.parse(JSON.stringify(pricesArr));
    const deepCloneOfCachedPrices = JSON.parse(JSON.stringify(cachedObject.prices));
    deepCloneOfCachedPrices.forEach((cachedPrice) => {
      if (cachedPrice.cash) {
        deepCloneOfPrices.forEach((price, index) => {
          if (cachedPrice.fuel_product === price.fuel_product) {
            deepCloneOfPrices[index].cash = cachedPrice.cash;
          }
        });
      }
      if (cachedPrice.credit) {
        deepCloneOfPrices.forEach((price, index) => {
          if (cachedPrice.fuel_product === price.fuel_product) {
            deepCloneOfPrices[index].credit = cachedPrice.credit;
          }
        });
      }
    });
    return deepCloneOfPrices;
  };

  const filterOutZeroPrices = (pricesArr) => {
    const deepCloneOfPrices = JSON.parse(JSON.stringify(pricesArr));
    deepCloneOfPrices.forEach((p) => {
      if (p.cash?.price === 0) {
        delete p.cash;
      }
      if (p.credit?.price === 0) {
        delete p.credit;
      }
    });
    return deepCloneOfPrices;
  };

  const zeroOutPricesState = (pricesArr) => {
    const deepCloneOfPrices = JSON.parse(JSON.stringify(pricesArr));
    deepCloneOfPrices.forEach((p) => {
      if (p.cash?.price) {
        p.cash.price = 0;
      }
      if (p.credit?.price) {
        p.credit.price = 0;
      }
    });
    return deepCloneOfPrices;
  };

  return (
    <CardWithEditModal
      title="PRICES"
      showCollapseButton
      showEditButton={showEditButton}
      isOpen={isEditing}
      onSave={handleOnSave}
      isLoading={isLoading}
      handleOnClose={handleOnModalClose}
      handleOnEditClick={toggleIsEditing}
      cardContent={(
        <FuelPriceContent
          country={station.address.country}
          isLoading={isLoading}
          prices={prices}
          currentStationPrices={currentStationPrices}
          fuelTypes={station.fuel_types}
          hasCashPrices={hasCashPrices}
          notifyToEditPrices={toggleIsEditing}
          showPricesBeingUpdated={showPricesBeingUpdated}
        />
      )}
      modalContent={(
        <>
          <FuelPriceContent
            isLoading={isLoading}
            prices={prices}
            currentStationPrices={currentStationPrices}
            fuelTypes={station.fuel_types}
            hasCashPrices={hasCashPrices}
            notifyToEditPrices={toggleIsEditing}
            onPriceBlur={onPriceBlur}
            isEditing={isEditing}
            country={station.address.country}
            showPricesBeingUpdated={showPricesBeingUpdated}
          />
          <div className={styles.recurringPriceCheckboxContainer}>
            <Checkbox
              label="Auto-update the price(s) for this price submission for today?"
              checked={recurringPrices}
              onChange={() => setRecurringPrices(!recurringPrices)}
              className={styles.recurringPriceCheckbox}
            />
          </div>
        </>
      )}
      saveConfirmationDialogProps={recurringPrices ? onSaveConfirmationDialogProps : null}
    />
  );
}

const mapStateToProps = state => ({
  station: state.station,
  isLoading: state.loading,
});

const mapDispatchToProps = dispatch => ({
  editStation: stationEdit => dispatch(editStation(stationEdit)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StationFuelPriceCard);

StationFuelPriceCard.propTypes = {
  isLoading: PropTypes.bool,
  station: StationPropType,
  editStation: PropTypes.func,
};

StationFuelPriceCard.defaultProps = {
  isLoading: false,
  station: null,
  editStation: null,
};
